import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import client from "@/lib/api";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { StatusBadge } from "@/components/shared";
import { User } from "lucide-react";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function GlobalSearch({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const search = useQuery({
    queryKey: ["global-search", q],
    queryFn: async () => (await client.get("/customers", { params: { search: q || undefined } })).data,
    enabled: open,
    staleTime: 10_000,
  });

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const go = (id) => { onOpenChange(false); setQ(""); navigate(`/customers/${id}`); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <DialogTitle className="sr-only">Search customers</DialogTitle>
      <DialogDescription className="sr-only">Search for a customer by name, phone or company and open their profile.</DialogDescription>
      <CommandInput placeholder="Search customers by name, phone or company…" value={q} onValueChange={setQ} data-testid="global-search-input" />
      <CommandList>
        {search.isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Searching…</div>
        ) : (
          <>
            <CommandEmpty>No customers found.</CommandEmpty>
            <CommandGroup heading="Customers">
              {(search.data || []).slice(0, 8).map((c) => (
                <CommandItem key={c.id} value={`${c.name} ${c.company || ""} ${c.phone || ""} ${c.id}`} onSelect={() => go(c.id)} data-testid={`search-result-${c.id}`} className="flex items-center gap-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground"><User className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{c.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{c.company || c.phone || c.email || "—"}</span>
                  </span>
                  <StatusBadge status={c.status} />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
